import { useTranslation } from "react-i18next";
import { IconChevronLeft } from "@tabler/icons-react";
import { Button } from "../../../components/ui/Button";
import { StepProgress } from "../../../components/ui/StepProgress";
import { OnboardingIdentityPanel } from "./OnboardingIdentityPanel";
import { OnboardingCountryField } from "./OnboardingCountryField";
import { OnboardingRoleFields } from "./OnboardingRoleFields";
import { RegistryFoundDetails } from "./RegistryFoundDetails";
import { RegistryNotFound } from "./RegistryNotFound";
import { RegistryVerifying } from "./RegistryVerifying";
import { IntroStep } from "../steps/IntroStep";
import { CountryStep } from "../steps/CountryStep";
import { NameStep } from "../steps/NameStep";
import { OtpStep } from "../steps/OtpStep";
import { ProfessionStep } from "../steps/ProfessionStep";
import { SpecialtyStep } from "../steps/SpecialtyStep";
import { PracticeStep } from "../steps/PracticeStep";
import { PracticeAgentPanel } from "../steps/PracticeAgentPanel";
import { RegistryStep } from "../steps/RegistryStep";
import { OnboardingStepActions } from "../components/OnboardingStepActions";
import { PartnerObjectionContent } from "../../../components/partners/PartnerObjectionContent";
import {
  PartnerObjectionSheets,
  useObjectionSheets,
  type ObjectionSheet,
} from "../../../components/partners/PartnerObjectionSheets";
import { updateOnboardingFlow } from "../../../domains/auth/stores/onboardingFlowStore";
import { cn } from "../../../lib/utils";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import {
  COUNTRY_SLIDE,
  OTP_SLIDE,
  INTRO_SLIDE,
  NAME_SLIDE,
  PARTNER_OBJECTION_FORM_ID,
  PARTNER_OBJECTION_SLIDE,
  PROFESSION_SLIDE,
  REGISTRY_SLIDE,
  SPECIALTY_SLIDE,
  PRACTICE_SLIDE,
  type OnboardingFlow,
} from "../useOnboardingFlow";

// ---------------------------------------------------------------------------
// Desktop web layout (Figma "D7.identite", "D7.pratiques") — the questions on
// the left, the practitioner badge they fill in on the right. A single active
// step (no carousel) in a column with a static header (back + progress), the
// step's title, the step, and its CTA; the badge panel is the only thing this
// layout adds to the flow, and it adds nothing to its logic. All of that lives
// in `useOnboardingFlow`; this owns the desktop chrome.
//
// The step COMPONENTS are mostly the mobile ones: the two platforms ask the
// same questions in the same order and differ in the chrome around them. The
// registry slide is the exception: three of its states have a desktop drawing
// of their own (searching, found, not found).
// ---------------------------------------------------------------------------

export function DesktopOnboardingPage({ flow }: { flow: OnboardingFlow }) {
  const { t } = useTranslation();
  const step = flow.step;
  const objectionSheets = useObjectionSheets();

  // The record the registry found, while it waits for its Continue. The badge
  // shows it already, as drawn (Figma "D7.identite.pays-selectionne"): the
  // screen asks "is this you?" by showing what being you would look like.
  const foundProfile =
    step === REGISTRY_SLIDE &&
    flow.registryState !== null &&
    flow.registryState.status === "found"
      ? flow.registryState.profile
      : null;

  // A screen of fields takes its CTA right under them (Figma
  // "D7.identite.default"). A screen of content (a lookup, a list) keeps it at
  // the bottom of the column, where it does not move as the content grows.
  const ctaFollowsFields =
    step === NAME_SLIDE ||
    (step === PROFESSION_SLIDE && flow.groupsQuestions) ||
    (step === REGISTRY_SLIDE &&
      flow.registryState !== null &&
      flow.registryState.status === "found");
  // The practice screens are drawn on a wider column (Figma "D7.pratiques"):
  // their rows carry long catalogue names, and the margins give way to them.
  const isPracticeScreen = step === PRACTICE_SLIDE;

  return (
    // The shell stops growing at the width the design is drawn at, so a wide
    // display gets margins rather than a question column pinned to its left
    // edge and an artwork panel stretched across the rest.
    <div className="flex h-dvh w-full justify-center bg-ds-canvas">
      <div className="flex w-full max-w-desktop-shell">
        {/* `overflow-y-auto`: a screen of fields does not scroll on its own
            (below), so on a short window the column does, and its CTA stays
            reachable. */}
        <main
          className={cn(
            "flex w-full min-w-0 flex-col gap-8 overflow-y-auto py-8 lg:w-desktop-onboarding-column lg:shrink-0",
            isPracticeScreen ? "px-12" : "px-24.5"
          )}
        >
          {/* Header: back + progress. The band always takes its space so the
              title below starts at the same height on every step; the intro
              slide empties it rather than removing it, because it has no back
              (its own CTAs are the only way forward) and no progress to report
              yet. Each control carries its own `invisible`: a parent one would
              lose to the button's `visible` in the class merge. The bar runs
              from the back button to the column's right edge, the edge of the
              fields under it. */}
          <div className="flex shrink-0 items-center gap-8">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("Back")}
              onClick={flow.handleBack}
              className={cn(
                "p-0 text-ds-black size-6 shrink-0",
                // Hide back only on the intro slide (its own CTAs are the
                // way forward) and while the account is being created. On the
                // first slide of a run it leaves onboarding.
                step === INTRO_SLIDE || flow.isFinalizing
                  ? "invisible pointer-events-none"
                  : "visible"
              )}
            >
              <IconChevronLeft className="size-full!" />
            </Button>
            <StepProgress
              totalSteps={flow.totalSteps}
              currentStep={flow.progressStep}
              variant="desktop"
              className={cn(
                "min-w-0 flex-1 py-1",
                step === INTRO_SLIDE && "invisible"
              )}
            />
            {/* Mounted on every slide so the bar keeps its width when the
                practice slide shows it. */}
            <button
              type="button"
              onClick={flow.handlePracticeSkip}
              className={cn(
                "h-6 shrink-0 cursor-pointer whitespace-nowrap border-none bg-transparent p-0 font-dm-sans text-sm font-medium text-ds-slate-600",
                flow.showsPracticeSkip
                  ? "visible"
                  : "invisible pointer-events-none"
              )}
            >
              {t("Later")}
            </button>
          </div>

          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col",
              isPracticeScreen ? "gap-5" : "gap-6"
            )}
          >
            {flow.stepHeader !== null && (
              <div className="flex shrink-0 flex-col gap-2">
                {/* Petrona, as the desktop design draws every question. The
                    mobile carousel keeps DM Sans. */}
                <h1 className="m-0 font-petrona text-desktop-step-title font-medium text-ds-mint-900">
                  {flow.stepHeader.title}
                </h1>
                {flow.stepHeader.subtitle !== null && (
                  <p className="m-0 font-dm-sans text-ui-body text-ds-slate-500">
                    {flow.stepHeader.subtitle}
                  </p>
                )}
              </div>
            )}

            {/* `scroll-fog-y` fades whichever edge still has content behind
                it, so a list that runs under the CTA reads as "there is more"
                rather than as a row someone cut in half. Scroll-driven, so it
                costs nothing where it is unsupported: the mask stays at 0.
                `min-h-full` is what makes a step's own `flex-1` mean
                anything: the intro renders its CTAs itself rather than through
                the footer, and without a full-height column to grow into they
                sit under the text instead of at the bottom. A screen of fields
                does not scroll: its dropdowns open over the CTA under them, as
                drawn, rather than inside a box that would cut them. The
                practice screen scrolls its list alone, under a chip and a
                count that stay put, so it gets a bounded box rather than a
                scrolling one, as the carousel gives it. */}
            {/* While the account is created in place, the screen it runs on
                stays drawn but takes no answer: a row tapped under the
                spinner would change what is being written. */}
            {isPracticeScreen ? (
              <fieldset
                disabled={flow.isFinalizing}
                className="m-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-0 p-0"
              >
                {renderCarouselStep(flow, objectionSheets.open)}
              </fieldset>
            ) : (
              <div
                className={cn(
                  ctaFollowsFields
                    ? "shrink-0"
                    : "min-h-0 flex-1 overflow-y-auto scroll-fog-y"
                )}
              >
                <fieldset
                  disabled={flow.isFinalizing}
                  className="m-0 flex min-h-full w-full min-w-0 flex-col border-0 p-0"
                >
                  {renderCarouselStep(flow, objectionSheets.open)}
                </fieldset>
              </div>
            )}

            {flow.showStepFooter && (
              <div className="flex w-full shrink-0 flex-col gap-2">
                <OnboardingStepActions flow={flow} variant="desktop" />
              </div>
            )}
          </div>
        </main>

        {foundProfile === null ? (
          <OnboardingIdentityPanel
            firstName={flow.firstName}
            lastName={flow.lastName}
            specialtyLabel={flow.effectiveSpecialtyLabel}
            registryId={
              flow.registryIdentity === null
                ? ""
                : flow.registryIdentity.identifier
            }
            registry={flow.registryPresentationSource}
            foundRegistry={flow.registryFoundSource}
            employer={flow.employer}
          />
        ) : (
          // The record's own name, not the one typed: the screen asks "is this
          // you?", and a namesake or a mistyped number is told apart by it.
          <OnboardingIdentityPanel
            // A record can carry no first name: the last name stands alone.
            firstName={
              foundProfile.firstName === null ? "" : foundProfile.firstName
            }
            lastName={foundProfile.lastName}
            specialtyLabel={foundProfile.specialtyLabel}
            registryId={
              foundProfile.identifier === null ? "" : foundProfile.identifier
            }
            registry={foundProfile.registry}
            foundRegistry={foundProfile.registry}
            employer={
              foundProfile.organizationName === null
                ? ""
                : foundProfile.organizationName
            }
          />
        )}
      </div>
      <PartnerObjectionSheets
        layout="desktop"
        openSheet={objectionSheets.openSheet}
        onClose={objectionSheets.close}
      />
    </div>
  );
}

// Only the active slide is rendered (no off-screen carousel on desktop); the
// step components are presentational and reseed from the flow store, so
// unmount/remount on navigation is lossless.
function renderCarouselStep(
  flow: OnboardingFlow,
  openObjectionSheet: (sheet: ObjectionSheet) => void
): React.JSX.Element | null {
  const step = flow.step;

  // Slide 1: Intro — what Juisci is + the healthcare-professional gate.
  if (step === INTRO_SLIDE) {
    return (
      <IntroStep
        onAccept={flow.handleIntroAccept}
        onDeclineHcp={flow.handleIntroDeclineHcp}
      />
    );
  }

  // Slide 2: Name
  if (step === NAME_SLIDE) {
    return (
      <NameStep
        firstName={flow.firstName}
        lastName={flow.lastName}
        onFirstNameChange={(v) => {
          updateOnboardingFlow({ firstName: v });
        }}
        onLastNameChange={(v) => {
          updateOnboardingFlow({ lastName: v });
        }}
        onNext={flow.handleNameNext}
        onSignupStarted={flow.handleSignupStarted}
        hasSignupStarted={flow.hasSignupStarted}
        isActive
        // Desktop asks the country here rather than on a slide of its own, so
        // this Continue submits both answers at once.
        countryField={
          flow.groupsQuestions ? (
            <OnboardingCountryField
              country={flow.country}
              onCountryChange={(v) => {
                flow.changeCountry(v);
              }}
            />
          ) : null
        }
        variant="desktop"
      />
    );
  }

  // Slide 0: Email code — the first slide an email signup answers
  if (step === OTP_SLIDE) {
    return (
      <OtpStep
        code={flow.otpCode}
        error={
          flow.otpState.status === "awaiting_code" ? flow.otpState.error : null
        }
        isBusy={
          flow.otpState.status === "sending" ||
          flow.otpState.status === "verifying"
        }
        cooldownRemaining={flow.otpCooldownRemaining}
        onCodeChange={flow.setOtpCode}
        onSubmit={flow.handleOtpSubmit}
        onResend={flow.handleOtpResend}
        // The desktop layout mounts the active step and nothing else.
        isActive
      />
    );
  }

  // Slide 4: Country
  if (step === COUNTRY_SLIDE) {
    return (
      <form
        id="onboarding-form-country"
        className="flex flex-col w-full"
        onSubmit={(e) => {
          e.preventDefault();
          flow.handleCountrySubmit();
        }}
      >
        <CountryStep
          country={flow.country}
          searchQuery={flow.countrySearch}
          onCountryChange={(v) => {
            flow.changeCountry(v);
          }}
        />
      </form>
    );
  }

  // Slide 5: RPPS lookup (French healthcare professionals only)
  if (step === REGISTRY_SLIDE) {
    if (flow.registryState === null) return null;
    if (flow.registry === null)
      throw new Error("Invariant: the registry slide has a registry");
    // The three states the desktop design draws (Figma "D7.identite"). The
    // others (a list of matches, a search to refine) keep the shared step.
    if (flow.registryState.status === "loading")
      return <RegistryVerifying registry={flow.registry} />;
    if (flow.registryState.status === "found") {
      const { profile } = flow.registryState;
      const matchedProfession =
        profile.matchedProfessionUid === null
          ? undefined
          : flow.professions.find(
              (profession) => profession.uid === profile.matchedProfessionUid
            );
      return (
        <RegistryFoundDetails
          firstName={profile.firstName === null ? "" : profile.firstName}
          lastName={profile.lastName}
          country={flow.country}
          roleLabel={
            matchedProfession === undefined
              ? profile.professionLabel
              : getItemTranslation(matchedProfession)
          }
          establishment={profile.organizationName}
        />
      );
    }
    if (
      flow.registryState.status === "not_found" &&
      flow.registry !== "reps" &&
      flow.registry !== "stps" &&
      flow.registry !== "big"
    )
      return (
        <RegistryNotFound
          registry={flow.registry}
          registryId={flow.registryId}
          onRegistryIdChange={flow.setRegistryId}
          onSubmitRegistryId={flow.submitRegistryId}
          onEditName={flow.handleBack}
        />
      );
    return (
      <RegistryStep
        stpsSearch={flow.registry === "stps" ? flow.stpsSearch : null}
        state={flow.registryState}
        registry={flow.registry}
        registryId={flow.registryId}
        registryInitials={flow.registryInitials}
        onRegistryIdChange={flow.setRegistryId}
        onRegistryInitialsChange={flow.setRegistryInitials}
        onSelectCandidate={flow.handleRegistrySelectCandidate}
        onLoadMore={flow.loadMoreRegistryCandidates}
        repsFirstName={flow.repsFirstName}
        repsFirstSurname={flow.repsFirstSurname}
        repsSecondSurname={flow.repsSecondSurname}
        repsResultsCurrent={flow.repsResultsCurrent}
        onRepsFirstNameChange={flow.setRepsFirstName}
        onRepsFirstSurnameChange={flow.setRepsFirstSurname}
        onRepsSecondSurnameChange={flow.setRepsSecondSurname}
      />
    );
  }

  // Slide 6: Profession — and, on the grouped form, the specialty with it.
  if (step === PROFESSION_SLIDE) {
    if (flow.groupsQuestions) {
      return (
        <OnboardingRoleFields
          professions={flow.professions}
          selectedProfession={flow.selectedProfession}
          onSelectProfession={(v) => {
            flow.selectProfession(v);
          }}
          specialties={flow.specialtiesForSelectedProfession}
          selectedSpecialty={flow.effectiveSpecialty}
          onSelectSpecialty={(v) => {
            flow.selectSpecialty(v);
          }}
          hasSpecialtyStep={flow.hasSpecialtyStep}
          onSubmit={flow.handleProfessionNext}
          professionList={flow.professionList}
          onShowMore={flow.showMoreProfessions}
        />
      );
    }
    return (
      <ProfessionStep
        professions={flow.professions}
        selected={flow.selectedProfession}
        searchQuery={flow.professionSearch}
        professionList={flow.professionList}
        onShowMore={flow.showMoreProfessions}
        onSelect={(v) => {
          flow.selectProfession(v);
        }}
        onNext={flow.handleProfessionNext}
      />
    );
  }

  // Slide 7: Specialty
  if (step === SPECIALTY_SLIDE) {
    if (flow.selectedProfession === null) return null;
    return (
      <SpecialtyStep
        specialties={flow.specialtyRows.rows}
        selectedSpecialty={flow.effectiveSpecialty}
        isAssisting={flow.specialtyRows.isAssisting}
        onSelectSpecialty={(v) => {
          flow.selectSpecialty(v);
        }}
        onSubmit={flow.handleSpecialtySubmit}
      />
    );
  }

  // Slide 8: Practice Profiles — only when the declared Specialty has a
  // catalogue, which for this lot is the two pilots and nobody else.
  if (step === PRACTICE_SLIDE) {
    // The agent is reading — a state of this step, under its own unchanged title
    // and footer. This is also what covers the fast track's gap, where the offer
    // for the just-confirmed Specialty is still in flight and whether there is
    // anything to confirm is not yet known.
    if (flow.practiceStatus === "inferring")
      return (
        <PracticeAgentPanel
          status="reading"
          firstName={flow.firstName}
          specialtyLabel={flow.effectiveSpecialtyLabel}
          variant="desktop"
        />
      );
    // What the agent found, read back. The list is one tap away and Continue
    // accepts this as it stands — untouched, the same evidence is what gets
    // written at finalize.
    if (flow.practiceStatus === "recap")
      return (
        <PracticeAgentPanel
          status="recap"
          names={flow.practiceRecapNames}
          onEdit={flow.handlePracticeEdit}
          onSubmit={flow.handlePracticeSubmit}
        />
      );
    if (flow.practiceOffer === null || !flow.hasPracticeStep) return null;
    return (
      <PracticeStep
        offer={flow.practiceOffer}
        selected={flow.practiceSelection}
        checkedOnOpen={flow.practiceCheckedOnOpen}
        disambiguation={flow.practiceDisambiguation}
        specialtyLabel={flow.effectiveSpecialtyLabel}
        phase={flow.practicePhase}
        declaration={flow.practiceDeclarationOffer}
        matching={flow.practiceAssist.matching}
        isAssisting={flow.practiceAssist.isAssisting}
        onToggle={flow.handlePracticeToggle}
        onToggleDisambiguation={flow.handlePracticeToggleDisambiguation}
        onSetPractices={flow.handlePracticeSet}
        onPhaseChange={flow.setPracticePhase}
        onChangeSpecialty={flow.handleChangeSpecialty}
        onSubmit={flow.handlePracticeSubmit}
        variant="desktop"
      />
    );
  }

  // Slide 9: Partner objection (Figma D7, 5588:236987), the last step. Its
  // Continue saves the choice, with the box checked or not, then finalizes the
  // account in place.
  if (step === PARTNER_OBJECTION_SLIDE) {
    return (
      <form
        id={PARTNER_OBJECTION_FORM_ID}
        className="flex w-full flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void flow.handlePartnerObjectionSubmit();
        }}
      >
        <PartnerObjectionContent
          layout="desktop"
          objected={flow.partnerObjected}
          onObjectedChange={(objected) => {
            updateOnboardingFlow({ partnerObjected: objected });
          }}
          onOpenPartners={() => {
            openObjectionSheet("partners");
          }}
          onOpenPrivacy={() => {
            openObjectionSheet("privacy");
          }}
        />
      </form>
    );
  }

  throw new Error(`Invariant: invalid carousel step index ${step}`);
}
